// deps
import { useCallback, useContext, useEffect, useState } from "react";

// internal-deps
import { AnalyticsContext } from "@swiggy-private/checkout-core";

// analytics events
import { impressionCartToast } from "@cart-client/analytics/events";

// contexts
import { CartToastContext, INITIAL_CART_TOAST_CONTEXT_VALUE } from "@cart-client/contexts/CartToastContext";

// helpers
import { getCartId } from "@cart-client/helpers/Cart";

// interfaces
import { ReviewedCart } from "@cart-client/interfaces/Cart";

export function useCartToast(cartResponse: ReviewedCart | null, isCroutonVisible: boolean): boolean {
    const { impressionEvent } = useContext(AnalyticsContext);
    const { cartToastData, setCartToastData } = useContext(CartToastContext);
    const [isCartToastActive, setCartToastVisibility] = useState(false);
    const [canSendImpression, setCanImpressionSend] = useState(false);

    const onCartToastImpression = useCallback(() => {
        const context = JSON.stringify({ msg: cartToastData.message });
        const cartId = cartResponse ? getCartId(cartResponse) : "";

        impressionCartToast(context, cartId, impressionEvent);
        setCanImpressionSend(false);
    }, [cartResponse, cartToastData.message, impressionEvent]);

    const resetCartToastData = useCallback(() => {
        setCartToastData(INITIAL_CART_TOAST_CONTEXT_VALUE.cartToastData);
    }, [setCartToastData]);

    const onToastDismiss = useCallback(
        (onDismiss?: VoidFunction) => {
            return () => {
                if (onDismiss != null) {
                    onDismiss();
                }
                resetCartToastData();
                setCartToastVisibility(false);
            };
        },
        [resetCartToastData],
    );

    useEffect(() => {
        if (!cartToastData.show) {
            isCartToastActive && setCartToastVisibility(false);
            return;
        }

        if (isCroutonVisible || isCartToastActive) {
            // reset toast data in case active crouton is visible
            isCroutonVisible && resetCartToastData();
            return;
        }

        // set toast dismiss handling before showing cart toast
        setCartToastData({
            ...cartToastData,
            onDismiss: onToastDismiss(cartToastData.onDismiss),
        });
        setCartToastVisibility(true);
        setCanImpressionSend(true);
    }, [
        isCartToastActive,
        cartToastData,
        cartToastData.show,
        isCroutonVisible,
        onToastDismiss,
        resetCartToastData,
        setCartToastData,
    ]);

    useEffect(() => {
        if (!canSendImpression) {
            return;
        }
        onCartToastImpression();
    }, [canSendImpression, onCartToastImpression]);

    return isCartToastActive;
}
